import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, InputGroup, InputGroupText } from "reactstrap";
import styles from "../css/pinverificationmodal.module.scss";

interface PinVerificationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onVerify: (password: string) => void;
  isLoading?: boolean;
  title?: string;
  description?: string | React.ReactNode;
  isInvalid?: boolean;
}

const PinVerificationModal: React.FC<PinVerificationModalProps> = ({
  isOpen,
  toggle,
  onVerify,
  isLoading = false,
  title = "E-Signature",
  description,
  isInvalid = false,
}) => {
  const [password, setPassword] = useState("");
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset and focus when modal opens
  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setIsAcknowledged(false);
      setShowPassword(false);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && password.trim() !== "" && isAcknowledged) {
      handleVerify();
    }
  };

  const handleVerify = () => {
    if (password.trim() !== "" && isAcknowledged) {
      onVerify(password);
    }
  };

  const isComplete = password.trim().length > 0 && isAcknowledged;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <i className="ri-pen-nib-line me-2 text-primary fs-20 align-middle" />
        <span className="align-middle fs-18">{title}</span>
      </ModalHeader>

      <ModalBody className="py-4">
        {/* Dummy hidden fields to trick password managers and browsers */}
        <div style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
          <input type="text" name={`fake_username_${Date.now()}`} tabIndex={-1} autoComplete="off" />
          <input type="password" name={`fake_password_${Date.now()}`} tabIndex={-1} autoComplete="off" />
        </div>

        <div className="mb-3 px-4">
          <div style={{
            backgroundColor: "#fff8e6",
            border: "1px solid #fde68a",
            borderRadius: "6px",
            padding: "12px 16px",
            color: "#b45309",
            fontSize: "14px",
            textAlign: "left",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px"
          }}>
            <i className="ri-error-warning-line fs-18" style={{ marginTop: "2px" }}></i>
            <div style={{ wordBreak: "break-word" }}>
              {description || 'Please enter your password to apply your E-Signature. This action is legally binding and will be recorded as your electronic signature on the record.'}
            </div>
          </div>
        </div>
        
        <div className="mx-auto mt-4" style={{ maxWidth: "380px" }}>
          <div className="text-start mb-1">
            <label className="form-label fw-bold text-dark mb-1">Password</label>
          </div>
          
          <InputGroup className="custom-input-group mb-2">
            <InputGroupText 
              style={{ 
                backgroundColor: '#fff',
                borderRadius: "10px 0 0 10px",
                border: isInvalid ? "1.5px solid #dc3545" : "1.5px solid #0d6efd",
                borderRight: 'none',
                paddingLeft: '15px'
              }}
            >
              <i className="ri-lock-2-line text-muted fs-18"></i>
            </InputGroupText>
            
            <input
              ref={inputRef}
              type={showPassword ? "text" : "password"}
              name="password"
              id="verify_password_field"
              className={`form-control ${styles.passwordInput} ${isInvalid ? 'is-invalid' : ''}`}
              placeholder="Enter password for E-Signature"
              value={password}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
              readOnly
              onFocus={(e) => {
                e.target.readOnly = false;
              }}
              style={{
                height: "50px",
                borderRadius: "0",
                fontSize: "16px",
                paddingLeft: "10px",
                border: isInvalid ? "1.5px solid #dc3545" : "1.5px solid #0d6efd",
                borderLeft: 'none',
                borderRight: 'none',
                outline: 'none',
                boxShadow: 'none'
              }}
            />
            
            <InputGroupText 
              onClick={() => setShowPassword(!showPassword)}
              style={{ 
                cursor: 'pointer', 
                backgroundColor: '#fff',
                borderRadius: "0 10px 10px 0",
                border: isInvalid ? "1.5px solid #dc3545" : "1.5px solid #0d6efd",
                borderLeft: 'none',
                paddingRight: '15px'
              }}
            >
              <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} ${isInvalid ? 'text-danger' : 'text-primary'} fs-18`}></i>
            </InputGroupText>
          </InputGroup>

          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="ri-checkbox-circle-line text-success fs-16"></i>
            <span className="text-muted fs-13">Your password is encrypted and secure.</span>
          </div>

          <div className="text-start mt-1 d-flex align-items-start gap-2">
            <input
              type="checkbox"
              id="signature_ack"
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
              className="form-check-input mt-1"
              style={{ cursor: "pointer" }}
            />
            <label htmlFor="signature_ack" className="form-check-label text-dark fs-14" style={{ cursor: "pointer" }}>
              I acknowledge that my credentials will be used as an electronic signature for this action.
            </label>
          </div>

          {isInvalid && (
            <div className="text-danger mt-2 fs-13 fw-medium text-start">
              <i className="ri-error-warning-line me-1"></i>
              Invalid password. Please try again.
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button 
          color="dark" 
          outline
          onClick={toggle} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          color="primary"
          onClick={handleVerify} 
          disabled={!isComplete || isLoading}
        >
          {isLoading ? (
            <><Spinner size="sm" className="me-2" />Applying...</>
          ) : (
            <><i className="ri-pen-nib-line me-1" />Apply E-Signature</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PinVerificationModal;

